import { createStart, createMiddleware } from "@tanstack/react-start";

import { renderErrorPage } from "./lib/error-page";
import { attachSupabaseAuth } from "@/integrations/supabase/auth-attacher";

const errorMiddleware = createMiddleware().server(async ({ next }) => {
  try {
    return await next();
  } catch (error) {
    if (error != null && typeof error === "object" && "statusCode" in error) {
      throw error;
    }
    console.error(error);
    return new Response(renderErrorPage(), {
      status: 500,
      headers: { "content-type": "text/html; charset=utf-8" },
    });
  }
});

// Add CDN caching for SSR HTML responses to improve TTFB / Core Web Vitals.
// The browser still revalidates (max-age=0), but the CDN can serve cached
// HTML for 5 minutes and use stale-while-revalidate for up to 1 day.
const cacheMiddleware = createMiddleware().server(async ({ request, next }) => {
  const result = await next();
  const response: Response | undefined =
    result instanceof Response ? result : (result as { response?: Response }).response;
  if (response && response.status === 200 && !response.headers.has("cache-control")) {
    const ct = response.headers.get("content-type") || "";
    if (ct.includes("text/html")) {
      const url = new URL(request.url);
      const p = url.pathname;
      // Strony spersonalizowane per-IP (initialFrom z geo CF) — nie wolno
      // serwować jednego HTML wszystkim. `/search` i `/admin` też wykluczone.
      const cacheable = p !== "/" && p !== "/tanie-loty"
        && !p.startsWith("/loty/kraj/") && !p.startsWith("/loty/temat/")
        && !p.startsWith("/admin") && !p.startsWith("/api/") && !p.startsWith("/search");
      if (cacheable) {
        response.headers.set("cache-control", "public, max-age=0, s-maxage=300, stale-while-revalidate=86400");
      }
    }
  }
  return result;
});

// Block search-engine indexing for admin pages on the HTTP layer too.
// Even jeśli robots.txt zostanie zignorowany lub strona wycieknie linkiem,
// X-Robots-Tag każe Google/Bing wykluczyć ją z indeksu.
const robotsMiddleware = createMiddleware().server(async ({ request, next }) => {
  const result = await next();
  const response: Response | undefined =
    result instanceof Response ? result : (result as { response?: Response }).response;
  if (response) {
    const p = new URL(request.url).pathname;
    if (p === "/admin" || p.startsWith("/admin/")) {
      response.headers.set("x-robots-tag", "noindex, nofollow, noarchive, nosnippet");
    }
  }
  return result;
});

export const startInstance = createStart(() => ({
  functionMiddleware: [attachSupabaseAuth],
  requestMiddleware: [errorMiddleware, cacheMiddleware, robotsMiddleware],
}));
